import type { Translation } from '../../types';
import type { dossiers as en } from '../en/dossiers';

export const dossiers: Translation<typeof en> = {
  title: 'Hồ sơ tên miền',
  subtitle: 'Mọi tên miền đang chờ hồ sơ để được kích hoạt: khách còn thiếu gì, hệ thống đã tự kiểm tra được gì, việc nào cần người duyệt.',
  note: 'Hệ thống chỉ tự duyệt khi mọi thông tin đều khớp (eKYC đạt, hoặc bản khai ký số đạt mọi kiểm tra). Các trường hợp còn lại nằm ở đây để nhân viên quyết định; không hồ sơ nào bị tự động từ chối, và đơn đang chờ duyệt không bị huỷ khi đến hạn.',
  stats: {
    awaiting: 'Chờ khách nộp',
    review: 'Cần duyệt tay',
    warning: 'Cảnh báo',
    dueSoon: 'Sắp hết hạn (< 24 giờ)',
    activatedToday: 'Đã kích hoạt hôm nay',
    checking: 'Đang tự kiểm tra',
    activating: 'Đã duyệt – đang kích hoạt',
  },
  board: { label: 'Cách xem', list: 'Danh sách', board: 'Theo giai đoạn', unassigned: 'Chưa phân công' },
  stage: {
    AWAITING_CUSTOMER: 'Chờ khách nộp hồ sơ',
    AUTO_CHECKING: 'Đang tự kiểm tra',
    NEEDS_REVIEW: 'Cần duyệt tay',
    WARNING: 'Cảnh báo',
    ACTIVATING: 'Đã duyệt – đang kích hoạt',
    ACTIVATED: 'Đã kích hoạt',
  },
  method: {
    EKYC: 'eKYC',
    VNEID: 'VNeID',
    DECLARATION_SIGNED: 'Bản khai ký số',
    DECLARATION_SCAN: 'Bản khai scan',
    DECLARATION: 'Bản khai',
    DOCUMENTS: 'Giấy tờ',
    STAFF: 'Nhân viên duyệt',
    none: 'Chưa có',
  },
  finding: {
    NOTHING_SUBMITTED: 'Khách chưa gửi gì',
    CORRECTION_REQUESTED: 'Đã yêu cầu bổ sung',
    VERIFICATION_FAILED: 'Lần xác thực gần nhất không đạt',
    CHECKING: 'Đang kiểm tra',
    NEEDS_STAFF: 'Cần nhân viên',
    WARNING: 'Cảnh báo',
    AUTO_APPROVED: 'Hệ thống tự duyệt ✓',
    STAFF_APPROVED: 'Nhân viên đã duyệt',
  },
  reason: {
    EKYC_MISMATCH: 'eKYC: thông tin hoặc khuôn mặt không khớp giấy tờ',
    EKYC_FAILED: 'eKYC không đạt (ảnh không dùng được, giấy tờ không hợp lệ, không qua kiểm tra người thật)',
    VERIFICATION_INCONCLUSIVE: 'Kết quả xác thực chưa đủ căn cứ để tự duyệt',
    DOSSIER_MANUAL_REVIEW: 'Chính sách yêu cầu nhân viên duyệt',
    DECLARATION_REVIEW_SCAN: 'Bản khai scan / ký tay: cần đối chiếu bằng mắt',
    DECLARATION_REVIEW_SIGNED: 'Bản khai ký số: có kiểm tra chưa đạt',
    DECLARATION_REVIEW_MATCHES: 'Mọi thông tin đọc được đều khớp: có thể duyệt ngay',
    MANUAL_ACTIVATION: 'Cần kích hoạt thủ công',
    SUBJECT_CHANGED_AFTER_DOSSIER: 'Chủ thể thay đổi sau khi hồ sơ hoàn tất',
    VNNIC_SYNC_MISSING: 'Chưa gửi hồ sơ sang VNNIC',
    VNNIC_SYNC_STUCK: 'Đồng bộ VNNIC bị treo',
    VNNIC_SYNC_REJECTED: 'VNNIC từ chối / yêu cầu sửa',
    VNNIC_SYNC_DRIFT: 'Hồ sơ khác bản đã gửi VNNIC',
    WORKFLOW_FAILED: 'Kích hoạt bị lỗi',
    WORKFLOW_MANUAL_REVIEW: 'Kích hoạt đang chờ người xử lý',
    changed: 'Đã đổi: {{parts}}',
    failedChecks: 'Kiểm tra chưa đạt: {{checks}}',
    syncError: '{{meaning}} ({{code}})',
    fields: '{{field}}: {{result}}',
  },
  code: {
    NAME_MISMATCH: 'Họ tên không khớp',
    BIRTH_MISMATCH: 'Ngày sinh không khớp',
    NUMBER_MISMATCH: 'Số giấy tờ không khớp',
    FACE_MISMATCH: 'Khuôn mặt không khớp ảnh giấy tờ',
    NAME_UNREADABLE: 'Không đọc được họ tên',
    BIRTH_UNREADABLE: 'Không đọc được ngày sinh',
    NUMBER_UNREADABLE: 'Không đọc được số giấy tờ',
    ID_UNREADABLE: 'Không đọc được giấy tờ',
    NAME_NOT_DECLARED: 'Chưa khai họ tên',
    BIRTH_NOT_DECLARED: 'Chưa khai ngày sinh',
    NUMBER_NOT_DECLARED: 'Chưa khai số giấy tờ',
    DOCUMENT_NOT_GENUINE: 'Giấy tờ không phải bản gốc hợp lệ',
    DOCUMENT_NOT_ACCEPTED: 'Loại giấy tờ không được chấp nhận',
    LIVENESS_FAILED: 'Không qua kiểm tra người thật',
    METHOD_NOT_ACCEPTED: 'Đã xác thực nhưng bằng phương thức chính sách không chấp nhận',
    VERSION_MISMATCH: 'Xác thực cho phiên bản chủ thể khác',
  },
  changedPart: { legal_identity: 'thông tin pháp lý', identifiers: 'số giấy tờ', addresses: 'địa chỉ', contacts: 'liên hệ' },
  subjectType: {
    INDIVIDUAL: 'Cá nhân',
    ORGANIZATION: 'Tổ chức',
    HOUSEHOLD_BUSINESS: 'Hộ kinh doanh',
    GOVERNMENT_ENTITY: 'Cơ quan nhà nước',
    FOREIGN_ENTITY: 'Tổ chức nước ngoài',
    OTHER: 'Khác',
  },
  operation: {
    DOMAIN_REGISTRATION: 'Đăng ký mới',
    DOMAIN_TRANSFER_IN: 'Chuyển về',
    DOMAIN_REGISTRANT_CHANGE: 'Đổi chủ thể',
    DOMAIN_NAME_CHANGE: 'Đổi tên / chuyển nhượng',
    none: 'Tên miền đang hoạt động',
  },
  col: {
    domain: 'Tên miền',
    customer: 'Khách hàng',
    registrant: 'Chủ thể',
    stage: 'Giai đoạn',
    method: 'Xác thực bằng',
    finding: 'Hệ thống ghi nhận',
    waiting: 'Chờ từ / hạn',
    assignee: 'Người xử lý',
    action: 'Thao tác',
  },
  deadline: {
    due: 'hạn {{when}}',
    overdue: 'đã quá hạn',
    paused: 'tạm dừng hạn trong khi nhân viên duyệt',
    none: 'không có hạn',
  },
  filter: {
    stage: 'Giai đoạn',
    open: 'Tất cả đang mở',
    method: 'Xác thực bằng',
    tld: 'TLD',
    tldPlaceholder: 'vn, com.vn…',
    assignee: 'Người xử lý',
    me: 'Của tôi',
    unassigned: 'Chưa giao',
    dueSoon: 'Chỉ hồ sơ sắp hết hạn (< 24 giờ)',
    search: 'Tên miền',
    searchPlaceholder: 'Tìm tên miền…',
    any: 'Tất cả',
  },
  action: {
    remind: 'Nhắc khách',
    review: 'Duyệt',
    open: 'Mở',
    view: 'Xem',
    next: 'Hồ sơ tiếp theo',
    assignMe: 'Giao cho tôi',
    assignOther: 'Giao cho…',
    note: 'Ghi chú',
    approve: 'Duyệt',
    reject: 'Yêu cầu bổ sung',
  },
  empty: 'Không có hồ sơ nào trong danh sách này.',
  emptyHint: 'Không có tên miền nào đang chờ hồ sơ theo bộ lọc này.',
  page: '{{from}}–{{to}} / {{total}}',
  hstm: {
    slot: 'Tệp bổ sung cho hồ sơ VNNIC',
    slotHint: 'Loại giấy tờ VNNIC HSTM yêu cầu mà hệ thống chưa có mẫu (cam kết từ khoá nhạy cảm, biên bản gov.vn, đơn đổi tên, …). PDF được kiểm tra chữ ký số tự động.',
    reason: 'Lý do (ghi vào nhật ký)',
    sensitiveKeyword: 'Tên miền chứa từ khoá nhạy cảm',
  },
  remind: {
    title: 'Nhắc khách hoàn tất hồ sơ',
    description: 'Gửi e-mail cho chủ thể (hoặc chủ tài khoản nếu chủ thể không có e-mail) rằng hồ sơ của {{domain}} còn thiếu, kèm hạn và cách hoàn tất (eKYC hoặc bản khai). Tối đa một lần mỗi 24 giờ cho mỗi hồ sơ; được ghi lại và audit.',
    note: 'Lời nhắn cho khách (không bắt buộc)',
    noteHint: 'Hiện trong e-mail, ví dụ hồ sơ còn thiếu gì.',
    submit: 'Gửi lời nhắc',
    sent: 'Đã gửi lời nhắc cho chủ thể.',
    tooSoon: 'Đã nhắc trong vòng 24 giờ qua. Có thể nhắc lại {{when}}.',
    lastSent: 'Nhắc lần cuối {{when}} (đã gửi {{count}} lần)',
  },
  panel: {
    title: 'Duyệt hồ sơ',
    loadError: 'Không tải được hồ sơ',
    order: 'Đơn hàng',
    operation: 'Nghiệp vụ',
    dossier: 'Hồ sơ',
    registrant: 'Chủ thể (phiên bản {{version}})',
    newerVersion: 'Chủ thể đã có phiên bản mới hơn phiên bản hồ sơ này đang dùng.',
    subjectType: 'Loại chủ thể',
    identifiers: 'Giấy tờ (đã che)',
    emails: 'E-mail',
    verifications: 'Xác thực',
    noVerifications: 'Chưa có xác thực nào cho phiên bản chủ thể này.',
    pinned: 'hồ sơ đang dùng',
    declarations: 'Bản khai',
    checks: 'Kiểm tra',
    documents: 'Giấy tờ',
    documentsHint: 'Mở giấy tờ trên trang của nó; mỗi lần xem phải nêu lý do và được audit.',
    history: 'Lịch sử hồ sơ',
    items: 'Việc liên quan',
    reminders: 'Lời nhắc đã gửi',
    noReminders: 'Chưa nhắc lần nào.',
    finding: 'Hệ thống ghi nhận',
    decisionHint: 'Duyệt ghi nhận quyết định qua việc cần duyệt (xác thực do nhân viên, hoặc chấp nhận bản khai): quy trình đang chờ sẽ hoàn tất hồ sơ và kích hoạt tên miền. Yêu cầu bổ sung trả hồ sơ về cho khách và gửi e-mail cho chủ thể.',
    noItem: 'Không có việc cần duyệt: không có gì để quyết định ở đây.',
    requestItem: 'Việc này được quyết định trên trang yêu cầu.',
    openItem: 'Mở việc cần xử lý',
    openDomain: 'Mở tên miền',
    openSubject: 'Mở chủ thể',
    openOrder: 'Mở đơn hàng',
    keyboard: 'Phím tắt: J / K hồ sơ sau / trước, Enter mở duyệt, A duyệt, R yêu cầu bổ sung, N hồ sơ tiếp theo sau khi quyết định.',
    decided: 'Đã ghi nhận quyết định. Chuyển sang hồ sơ tiếp theo.',
    lastItem: 'Đã ghi nhận quyết định. Danh sách không còn hồ sơ nào.',
    noteTitle: 'Thêm ghi chú',
    noteHint: 'Ghi chú nội bộ, không gửi cho khách; được audit (không lưu nội dung vào nhật ký).',
  },
  ops: {
    chip: 'Vận hành',
    hidden: 'Đang ẩn {{count}} việc vận hành (registry, đối soát, e-mail, webhook, quy trình).',
    show: 'Hiện',
    openHub: 'Mở hub Vận hành',
    showing: 'Đang chỉ hiện việc vận hành.',
    back: 'Quay lại việc hồ sơ',
  },
  badge: '{{count}} hồ sơ cần duyệt tay',
};
